import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FreestyleCombobox } from '@/components/ui/freestyle-combobox';
import { Address2Input } from './Address2Input';
import { useUniqueCities } from '@/hooks/useLocations';
import { useMemo } from 'react';

const US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

interface AddressInputProps {
  value?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  onChange: (address: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zip?: string;
  }) => void;
  disabled?: boolean;
  required?: boolean;
  showLabels?: boolean;
  layout?: 'compact' | 'standard';
}

export function AddressInput({
  value = {},
  onChange,
  disabled = false,
  required = false,
  showLabels = true,
  layout = 'standard',
}: AddressInputProps) {
  // Fetch ALL cities from the database, not filtered by client
  const { data: cities, isLoading } = useUniqueCities();

  // Format cities for the combobox
  const cityOptions = useMemo(() => {
    if (!cities || cities.length === 0) {
      return [];
    }

    return cities.map((city) => ({
      value: city,
      label: city,
    }));
  }, [cities]);

  const updateField = (field: string, newValue: string) => {
    onChange({
      ...value,
      [field]: newValue,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        {showLabels && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address Line 1 {required && <span className="text-red-600">*</span>}
          </label>
        )}
        <Input
          value={value.addressLine1 || ''}
          onChange={(e) => updateField('addressLine1', e.target.value)}
          placeholder="Address Line 1"
          disabled={disabled}
        />
      </div>

      <div>
        {showLabels && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address Line 2
          </label>
        )}
        <Address2Input
          value={value.addressLine2 || ''}
          onChange={(v) => updateField('addressLine2', v)}
          disabled={disabled}
        />
      </div>

      {/* Six columns leave the ZIP about 50px wide on a phone, so below `sm` the
          standard layout drops to two: city on its own row, state and ZIP
          sharing the next. */}
      <div className={layout === 'compact' ? 'grid grid-cols-2 gap-4' : 'grid grid-cols-2 gap-4 sm:grid-cols-6'}>
        <div className={layout === 'compact' ? 'col-span-2' : 'col-span-2 sm:col-span-3'}>
          {showLabels && (
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City {required && <span className="text-red-600">*</span>}
            </label>
          )}
          <FreestyleCombobox
            value={value.city || ''}
            onValueChange={(newValue) => updateField('city', newValue)}
            options={cityOptions}
            placeholder="Select or type city"
            searchPlaceholder="Search or type city..."
            emptyText={isLoading ? "Loading cities..." : "Type to enter a city"}
            disabled={disabled}
          />
        </div>

        <div className={layout === 'compact' ? '' : 'sm:col-span-2'}>
          {showLabels && (
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State {required && <span className="text-red-600">*</span>}
            </label>
          )}
          <Select
            value={value.state || 'CA'}
            onValueChange={(newValue: string) => updateField('state', newValue)}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((state) => (
                <SelectItem key={state} value={state}>
                  {state}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className={layout === 'compact' ? '' : 'sm:col-span-1'}>
          {showLabels && (
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ZIP
            </label>
          )}
          <Input
            value={value.zip || ''}
            onChange={(e) => updateField('zip', e.target.value)}
            placeholder="ZIP"
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}
